import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    IconButton,
    Button,
    Tooltip,
    Typography,
    useTheme,
    List,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Save,
    Delete,
    Settings,
    Chat,
    ExitToApp,
    Person,
    GridView,
    DarkMode,
    LightMode,
    ChatOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logout } from '../reducers/authReducer';
import MenuDrawer from './MenuDrawer';
import {
    clearSelectedTileId,
    setShowTiles,
    setIsActiveChat,
    clearPromptItems,
    setIsDarkTheme,
} from '../reducers/uiReducer';
import { clearMessages } from '../reducers/messagesReducer';
import { setSelectedDocument } from '../reducers/documentsReducer';
import { setSelectedDataset } from '../reducers/datasetsReducer';

const iconComponents = {
    Save: <Save />,
    Delete: <Delete />,
    Settings: <Settings />,
    Menu: <MenuIcon />,
    Chat: <Chat />,
    Logout: <ExitToApp />,
    Person: <Person />,
    Grid: <GridView />,
    ChatOutlined: <ChatOutlined />,
    DarkMode: <DarkMode />,
    LightMode: <LightMode />
};

const ActionBar = ({ config, onShowTiles }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme(); // 🌓 Detect theme
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const isDarkTheme = useSelector((state) => state.ui.isDarkTheme);
    const showTiles = useSelector((state) => state.ui.showTiles);

    const toggleDrawer = (open) => (event) => {

        if (
            event.type === 'keydown' &&
            (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleNewChat = () => {
        dispatch(clearMessages());
        dispatch(clearSelectedTileId());
        dispatch(setSelectedDataset(null));
        dispatch(setSelectedDocument(null));
        dispatch(setShowTiles(true));
        dispatch(setIsActiveChat(false));
        dispatch(clearPromptItems());
        navigate('/');
    };

    return (
        <Box
            sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                p: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                height: '56px',
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(to top,rgb(41, 41, 44),rgb(57, 57, 59),rgb(70, 70, 71))'
                    : 'linear-gradient(to top, #ffffff, rgb(238, 241, 247), #d1d5db)',
                boxShadow: 1,
            }}
        >
            {/* Left Actions */}
            <Box>
                {config.leftActions.map((action) => {
                    if (action.type === 'menu') {
                        return (
                            <IconButton
                                size="small"
                                onClick={toggleDrawer(true)}
                                sx={{
                                    mr: 5,

                                }}
                            >
                                {config.appLogo}
                            </IconButton>
                        );
                    }
                    return null;
                })}

                <MenuDrawer
                    drawerOpen={drawerOpen}
                    toggleDrawer={toggleDrawer}
                />

            </Box>

            {/* App Title and Logo */}
            <Button
                onClick={() => navigate('/')}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    textTransform: 'none',
                }}
            >
                <Typography variant="h6" sx={{ mr: 1 }}>
                    {config.appTitle}
                </Typography>
            </Button>

            {/* Right Actions */}
            <Box>
                {config.rightActions.map((action) => {
                    if (action.type === 'button') {
                        return (
                            <Button
                                key={action.id}
                                variant={action.variant || 'contained'}
                                color={action.color || 'primary'}
                                onClick={action.onClick}
                                startIcon={iconComponents[action.icon] || null}
                            >
                                {action.label}
                            </Button>
                        );
                    }

                    if (action.type === 'icon') {
                        const handleClick = () => {
                            if (action.id === 'show_tiles') {
                                navigate('/');
                                onShowTiles?.(!showTiles);
                            } else if (action.id === 'profile_info') {
                                navigate('/profile');
                            }
                            else if (action.id === 'toggle_theme') {

                                dispatch(setIsDarkTheme(!isDarkTheme));
                            } else if (action.onClick) {
                                action.onClick();
                            }
                        };

                        return (
                            <Tooltip key={action.id} title={action.tooltip || ''}>
                                <IconButton color={action.color || 'default'} onClick={handleClick}>
                                    {action.id === 'toggle_theme' ? (
                                        isDarkTheme ? (
                                            <LightMode />
                                        ) : (
                                            <DarkMode />
                                        )
                                    ) : action.id === 'show_tiles' ? (
                                        showTiles ? (
                                            <ChatOutlined />
                                        ) : (
                                            <GridView />
                                        )
                                    ) : (
                                        iconComponents[action.icon]
                                    )}
                                </IconButton>
                            </Tooltip>
                        );
                    }

                    return null;
                })}
            </Box>
        </Box>
    );
};

export default ActionBar;
